import { z } from "zod";
import { draftPurposeSchema } from "../enums.js";
import { nonEmptyStringSchema, uuidSchema } from "../primitives.js";
import { claimInputSchema } from "../schemas/claim.js";
import { createEmailDraftInputSchema } from "../schemas/email-draft.js";
import { createResearchJobInputSchema } from "../schemas/user.js";
import type { ToolName } from "./names.js";

/** Phase-1 tool input contracts (handlers land in later WPs). */

export const getResearchJobInputSchema = z.object({
  research_job_id: uuidSchema,
});

export const cancelResearchJobInputSchema = z.object({
  research_job_id: uuidSchema,
});

export const resumeResearchJobInputSchema = z.object({
  research_job_id: uuidSchema,
});

export const discoverHotelsInputSchema = z.object({
  research_job_id: uuidSchema,
});

export const searchWebInputSchema = z.object({
  query: nonEmptyStringSchema,
  max_results: z.number().int().min(1).max(20).optional(),
  research_job_id: uuidSchema.optional(),
});

export const crawlWebsiteInputSchema = z.object({
  url: z.string().url(),
  research_job_id: uuidSchema.optional(),
  candidate_id: uuidSchema.optional(),
  max_depth: z.number().int().min(0).max(2).optional(),
  max_pages: z.number().int().min(1).max(8).optional(),
});

export const extractHotelProfileInputSchema = z
  .object({
    artifact_id: uuidSchema,
    candidate_id: uuidSchema.optional(),
    hotel_id: uuidSchema.optional(),
    fields: z.array(nonEmptyStringSchema).optional(),
  })
  .refine((v) => v.candidate_id != null || v.hotel_id != null, {
    message: "candidate_id or hotel_id is required",
  });

export const findContactsInputSchema = z
  .object({
    hotel_id: uuidSchema.optional(),
    candidate_id: uuidSchema.optional(),
    research_job_id: uuidSchema.optional(),
  })
  .refine((v) => v.hotel_id != null || v.candidate_id != null, {
    message: "hotel_id or candidate_id is required",
  });

export const searchHotelsInputSchema = z.object({
  query: nonEmptyStringSchema.optional(),
  status: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

export const getHotelInputSchema = z.object({
  hotel_id: uuidSchema,
});

export const upsertHotelInputSchema = z.object({
  name: nonEmptyStringSchema,
  website: z.string().url().optional().nullable(),
  domain: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  research_job_id: uuidSchema.optional(),
});

export const scoreLeadInputSchema = z.object({
  hotel_id: uuidSchema,
  icp_profile_id: z.string().optional(),
  research_job_id: uuidSchema.optional(),
});

export const updateEmailDraftInputSchema = z.object({
  draft_id: uuidSchema,
  subject: nonEmptyStringSchema.optional(),
  body_text: nonEmptyStringSchema.optional(),
  purpose: draftPurposeSchema.optional(),
});

export const approveEmailDraftInputSchema = z.object({
  draft_id: uuidSchema,
});

export const rejectEmailDraftInputSchema = z.object({
  draft_id: uuidSchema,
  reason: z.string().optional(),
});

export const addNoteInputSchema = z.object({
  entity_type: nonEmptyStringSchema,
  entity_id: uuidSchema.optional().nullable(),
  kind: nonEmptyStringSchema.optional(),
  content: nonEmptyStringSchema,
});

export const createTaskInputSchema = z.object({
  title: nonEmptyStringSchema,
  description: z.string().optional(),
  due_at: z.string().datetime({ offset: true }).optional(),
  hotel_id: uuidSchema.optional(),
  contact_id: uuidSchema.optional(),
  research_job_id: uuidSchema.optional(),
  conversation_id: uuidSchema.optional(),
});

export const saveClaimsToolInputSchema = z.object({
  hotel_id: uuidSchema,
  claims: z.array(claimInputSchema).min(1),
  research_job_id: uuidSchema.optional(),
  agent_run_id: uuidSchema.optional(),
});

/**
 * Map of every Phase-1 tool name → Zod input schema.
 * Adding a TOOL_NAMES entry without a mapping must fail tests.
 */
export const TOOL_INPUT_SCHEMAS = {
  create_research_job: createResearchJobInputSchema,
  get_research_job: getResearchJobInputSchema,
  cancel_research_job: cancelResearchJobInputSchema,
  resume_research_job: resumeResearchJobInputSchema,
  discover_hotels: discoverHotelsInputSchema,
  search_web: searchWebInputSchema,
  crawl_website: crawlWebsiteInputSchema,
  extract_hotel_profile: extractHotelProfileInputSchema,
  find_contacts: findContactsInputSchema,
  search_hotels: searchHotelsInputSchema,
  get_hotel: getHotelInputSchema,
  upsert_hotel: upsertHotelInputSchema,
  save_claims: saveClaimsToolInputSchema,
  score_lead: scoreLeadInputSchema,
  create_email_draft: createEmailDraftInputSchema,
  update_email_draft: updateEmailDraftInputSchema,
  approve_email_draft: approveEmailDraftInputSchema,
  reject_email_draft: rejectEmailDraftInputSchema,
  add_note: addNoteInputSchema,
  create_task: createTaskInputSchema,
} as const satisfies Record<ToolName, z.ZodType>;

export type ToolInputSchemas = typeof TOOL_INPUT_SCHEMAS;

export function getToolInputSchema(name: ToolName): z.ZodType {
  return TOOL_INPUT_SCHEMAS[name];
}
